import ReviewCard from 'cc/ReviewCard'
import { useMemo } from 'react'
import { days } from 'common/utils/dates'
import { compact, uniq } from 'lodash'
import {
  useNormalizedHashtagPacket,
  plur,
  stringToHashtag,
  cn,
} from 'common/utils'
import routes from 'common/routes'
import TimeAgo from 'cc/TimeAgo'

import { EmptySection, EmptyPage } from 'components/Empty'
import config from 'common/config'
import { UserCard } from 'components/Cards'
import OpenGraph from 'components/OpenGraph'
import { handleFallback } from 'utils/pages'
import TabbedLayout from 'components/PageLayouts/TabbedLayout'

import {
  getMostCommonUserHashtags,
  getMostCommonDomainReviewHashtags,
  getUsersForCollection,
  getTaggedDomainReviews,
  getTaggedPageReviews,
  getTaggedUsers,
  getHashtag,
} from 'utils/hooks'
import { useCallback } from 'react'
import DomainLogo from 'components/DomainLogo'
import { partsFromPageKey } from 'common/logic'

const TaggedDomainReviews = ({ tag, domainReviews, reviewUsers, numTotal }) => {
  if (!domainReviews) {
    return <EmptySection>Loading...</EmptySection>
  }

  if (!domainReviews.length) {
    return <EmptySection>No site reviews tagged #{tag}</EmptySection>
  }

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      {domainReviews.map((review) => (
        <ReviewCard
          key={review.id}
          review={review}
          className="bg-white shadow-md rounded-md"
          user={reviewUsers.find((user) => user.id === review.meta.userId)}
          title={
            <>
              <DomainLogo
                domainKey={review.meta.domainKey}
                size={30}
                className="inline mr-1 rounded"
              />
              {review.meta.domainKey}
            </>
          }
        />
      ))}
    </div>
  )
}

const pageTitle = (domainKey, pageKey) => {
  if (!pageKey?.length) return null

  // TODO: really should show subdomain/etc. if set...
  const { path } = partsFromPageKey(pageKey)

  return (
    <div className="mx-4 flex items-center">
      <DomainLogo
        domainKey={domainKey}
        size={44}
        className="inline mr-2 rounded"
      />
      <div className="flex flex-col items-start leading-tight">
        <div className="text-gray-700">{domainKey}</div>
        <div className="text-gray-500 text-sm break-all">{path}</div>
      </div>
    </div>
  )
}

const TaggedPageReviews = ({ tag, pageReviews, reviewUsers, numTotal }) => {
  if (!pageReviews) {
    return <EmptySection>Loading...</EmptySection>
  }

  if (!pageReviews.length) {
    return <EmptySection>No page reviews tagged #{tag}</EmptySection>
  }

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      {pageReviews.map((review) => (
        <ReviewCard
          key={review.id}
          review={review}
          className="bg-white shadow-md rounded-md"
          user={reviewUsers.find((user) => user.id === review.meta.userId)}
          title={pageTitle(review.meta.domainKey, review.meta.pageKey)}
        />
      ))}
    </div>
  )
}

const TaggedUsers = ({ tag, users, numTotal }) => {
  // TODO: restyle this (and inline below, this was holdover from old GenericFeed usage)
  const ItemComponent = useCallback(
    ({ item: user }) => (
      <div key={user.id}>
        <UserCard user={user}>
          <p>{user.tagline}</p>
        </UserCard>
      </div>
    ),
    [],
  )

  if (!users) {
    return <EmptySection>Loading...</EmptySection>
  }

  if (!users.length) {
    return <EmptySection>No users tagged #{tag}</EmptySection>
  }

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      {users.map((user) => (
        <ItemComponent key={user.id} item={user} />
      ))}
    </div>
  )
}

function generateDescription(
  tag,
  createdAt,
  numDomainReviews,
  numUsers,
  byline,
) {
  return [
    `#${tag} was first seen ${(createdAt.toDate
      ? createdAt.toDate()
      : createdAt
    ).toLocaleDateString()}`,
    numDomainReviews || numUsers ? `. It has been ${byline}` : '.',
  ].join('')
}

function generateByline(
  numDomainReviews,
  numUsers,
  numPageReviews,
  numDomains,
) {
  return numDomainReviews || numUsers
    ? compact([
        numDomainReviews || numPageReviews
          ? `used in ${compact([
              numDomainReviews && plur(numDomainReviews, 'site review'),
              numPageReviews && plur(numPageReviews, 'page review'),
            ]).join(' and ')} ${numDomains === 1 ? 'on' : 'across'} ${plur(
              numDomains || 1,
              'domain',
            )}`
          : null,
        numDomainReviews && numUsers && ' (and ',
        numUsers ? `mentioned in ${plur(numUsers, 'user profile')}` : null,
        numDomainReviews && numUsers && ')',
        ' since.',
      ]).join('')
    : '.'
}

const HashtagPageContent = ({
  tag,
  tagInfo,
  taggedDomainReviews,
  taggedPageReviews,
  reviewUsers,
  taggedUsers,
}) => {
  const { numDomains, numDomainReviews, numPageReviews, numUsers, createdAt } =
    useNormalizedHashtagPacket(tagInfo)

  const byline = generateByline(
    numDomainReviews,
    numUsers,
    numPageReviews,
    numDomains,
  )

  const desc = generateDescription(
    tag,
    createdAt,
    numDomainReviews,
    numUsers,
    byline,
  )

  return (
    <>
      <OpenGraph
        url={routes.hashtagUrl(tag)}
        title={[`#${tag}`, config.appName].join(' on ')}
        description={desc}
      />
      <div className="md:mx-2 my-3">
        <h1 className="my-1 text-xl md:text-2xl font-light">#{tag}</h1>
        <h3 className="italic text-gray-500">
          First seen <TimeAgo datetime={createdAt} />
          {numDomainReviews || numUsers ? `, ${byline}` : '.'}
        </h3>
      </div>

      <TabbedLayout
        tabs={[
          ['Site Reviews', numDomainReviews],
          ['Page Reviews', numPageReviews],
          ['User Profiles', numUsers],
        ]}
        panelClassName="mt-5"
      >
        <TaggedDomainReviews
          tag={tag}
          numTotal={numDomainReviews}
          domainReviews={taggedDomainReviews}
          reviewUsers={reviewUsers}
        />

        <TaggedPageReviews
          tag={tag}
          numTotal={numPageReviews}
          pageReviews={taggedPageReviews}
          reviewUsers={reviewUsers}
        />

        <TaggedUsers tag={tag} numTotal={numUsers} users={taggedUsers} />
      </TabbedLayout>
    </>
  )
}

const HashtagPage = ({ tag, rawTag, tagInfo, ...props }) => {
  if (!tag?.length) {
    return (
      <EmptyPage title="Invalid Hashtag">
        Hmm.... looks like <mark>{rawTag}</mark> is not a valid hashtag?
      </EmptyPage>
    )
  }

  if (!tagInfo?.exists) {
    return (
      <EmptyPage className="my-3 mt-4">
        Nothing to show - #{tag} has not yet been used on {config.appName}.
      </EmptyPage>
    )
  }

  return <HashtagPageContent tag={tag} tagInfo={tagInfo} {...props} />
}

export async function getStaticPaths() {
  // TODO: preload for pageReviews too?
  // const reviewTags = await getMostCommonDomainReviewHashtags(30)
  // const userTags = await getMostCommonUserHashtags(30)

  // const tags = uniq([
  //   ...reviewTags.map((tag) => tag.id),
  //   ...userTags.map((tag) => tag.id),
  // ])

  // TODO: skipping until we have real content
  const tags = []

  const paths = tags.map((rawTag) => ({
    params: { rawTag },
  }))

  // No point showing the "loading" breadcrumb, just wait until we finish generating [OUTDATED - no more breadcrumbs at all]
  return { paths, fallback: 'blocking' }
}

export async function getStaticProps({ params }) {
  const tag = stringToHashtag(params.rawTag)
  const tagInfo = await getHashtag(tag)
  const [taggedUsers, taggedDomainReviews, taggedPageReviews] =
    await Promise.all([
      getTaggedUsers(tagInfo),
      getTaggedDomainReviews(tagInfo),
      getTaggedPageReviews(tagInfo),
    ])

  const reviewUsers = await getUsersForCollection([
    ...(taggedDomainReviews || []),
    ...(taggedPageReviews || []),
  ])

  return {
    props: {
      rawTag: params.rawTag,
      tag,
      tagInfo,
      taggedDomainReviews,
      taggedPageReviews,
      reviewUsers,
      taggedUsers,
    },
    revalidate: 1 * days,
  }
}

HashtagPage.fullscreen = true
HashtagPage.domainLogos = true

export default HashtagPage

// TODO: uncomment this if we switch from {fallback: 'blocking'} to {fallback: true}
// export default handleFallback(HashtagPage, {
//   Loader: () => (
//     <EmptyPage title="Loading...">Loading hashtag data...</EmptyPage>
//   ),
// })
